import { IInRestaurantIncentiveInput, IInRestaurantOrderLineItem } from 'generated/graphql-gateway';
import { isCombo, isItem } from 'utils/menu/is-menu-type';
import { blockContentToPlainText } from 'utils/sanity';
import { PosVendors } from 'utils/vendor-config';

import {
  IInRestaurantRedemptionCart,
  IInRestaurantRedemptionCartEntry,
  IInRestaurantRedemptionEntryType,
  IInRestaurantRedemptionOfferEntry,
  IOfferRedemption,
  IRewardRedemption,
  isRewardCartEntry,
} from './hooks/use-in-restaurant-redemption-cart';

const getVendorConfigsByPosVendor = (
  cartEntry: IInRestaurantRedemptionCartEntry,
  posVendor: PosVendors | null
): IInRestaurantOrderLineItem['vendorConfigs'] => {
  let vendorConfigs = cartEntry.vendorConfigs;

  if (posVendor === PosVendors.NCR && cartEntry.type === IInRestaurantRedemptionEntryType.REWARD) {
    const { reward } = cartEntry.details as IRewardRedemption;
    // only the first incentive is the needed to send
    const incentive = reward.incentives?.[0];
    if (incentive) {
      if (isItem(incentive) && incentive?.vendorConfigs) {
        vendorConfigs = incentive.vendorConfigs;
      } else if (isCombo(incentive) && incentive?.mainItem?.vendorConfigs) {
        vendorConfigs = incentive?.mainItem?.vendorConfigs;
      }
    }
  }

  return vendorConfigs;
};

const getOfferDetail = (
  cartEntry: IInRestaurantRedemptionCartEntry
): IOfferRedemption['offer'] | null => {
  if (isRewardCartEntry(cartEntry)) {
    return null;
  }

  const {
    details: { offer },
  } = cartEntry as IInRestaurantRedemptionOfferEntry;

  return offer;
};

const getIncentivePluFromCartEntry = (
  cartEntry: IInRestaurantRedemptionCartEntry
): Pick<IInRestaurantOrderLineItem, 'incentivePlu'> | null => {
  const { shortCode } = getOfferDetail(cartEntry) || {};

  return shortCode ? { incentivePlu: shortCode } : null;
};

const getNameFromCartEntry = (
  cartEntry: IInRestaurantRedemptionCartEntry
): Pick<IInRestaurantIncentiveInput, 'name'> | null => {
  const offer = getOfferDetail(cartEntry);
  if (!offer) {
    return null;
  }
  const stringOfferName = blockContentToPlainText(offer?.name?.localeRaw || []);

  return { name: stringOfferName };
};

export const generateOrderLineItems = (
  cart: IInRestaurantRedemptionCart,
  posVendor: PosVendors | null
): Array<IInRestaurantOrderLineItem> =>
  cart.map((cartEntry: IInRestaurantRedemptionCartEntry) => {
    const { quantity, referenceId, incentiveId, type } = cartEntry;

    return {
      incentive: {
        id: incentiveId,
        ...getNameFromCartEntry(cartEntry),
        type,
      },
      ...getIncentivePluFromCartEntry(cartEntry),
      quantity,
      referenceId,
      vendorConfigs: getVendorConfigsByPosVendor(cartEntry, posVendor),
    };
  });
